import { ShieldAlert } from 'lucide-react'
import { Outlet } from 'react-router-dom'
import type { ReactNode } from 'react'
import { useAuth } from '@/auth/useAuth'
import { LinkButton } from '@/components/ui/Button'
import type { Permission } from '@/auth/permissions'

type RequirePermissionProps = {
  permission: Permission
  /** What the user was trying to do, e.g. "edit products". Sharpens the message. */
  action?: string
  /** Omit to use as a route element — the matched child renders via <Outlet />. */
  children?: ReactNode
}

/**
 * Hides a screen from staff whose role does not include it.
 *
 * PRESENTATION ONLY. The API rejects the same calls with 403, which is what actually protects the
 * data. What this adds is an explanation: a staff member who follows a bookmark to a screen they
 * no longer have gets a sentence naming the access they need, rather than a page that renders
 * and then fills with failed requests.
 */
export function RequirePermission({ permission, action, children }: RequirePermissionProps) {
  const { can, user } = useAuth()

  if (!can(permission)) {
    return <AccessDenied action={action} roleName={user?.roleName ?? null} />
  }

  return children ? <>{children}</> : <Outlet />
}

function AccessDenied({ action, roleName }: { action?: string; roleName: string | null }) {
  return (
    <div className="mx-auto max-w-md py-16 text-center">
      <ShieldAlert className="mx-auto size-10 text-ink-subtle" aria-hidden />
      <h1 className="mt-4 text-lg font-semibold text-ink">You do not have access to this</h1>
      <p className="mt-2 text-sm text-ink-subtle">
        {action ? `${action} is not part of ` : 'This screen is not part of '}
        {/* Naming the role, not the permission: the person who can fix this fixes it by editing
            a role, so the role is the useful noun to carry into that conversation. */}
        {roleName ? <span className="font-medium text-ink">{roleName}</span> : 'your role'}. Ask a
        super admin if you need it.
      </p>
      <div className="mt-6">
        <LinkButton to="/">Back to the dashboard</LinkButton>
      </div>
    </div>
  )
}
